import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";
import {
  useCreateHaafPayment,
  useUpdateHaafPayment,
} from "@/api/haaf-payments";
import { useChapters } from "@/api/chapters";
import { currencies } from "@/lib/constants";

export function AddHaafPaymentForm({ initialData, onSuccess }) {
  const isEditMode = !!initialData;
  const { data: chapters, isLoading: isLoadingChapters } = useChapters();
  const createMutation = useCreateHaafPayment();
  const updateMutation = useUpdateHaafPayment();
  const [batch, setBatch] = useState(initialData?.batch || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const form = useForm({
    defaultValues: {
      payee: initialData?.payee || "",
      phoneNumber: initialData?.phoneNumber || "",
      amount: initialData?.amount || "",
      currency: initialData?.currency || "INR",
      batch: initialData?.batch || "",
      chapter: initialData?.chapter?._id || "",
      remarks: initialData?.remarks || "",
      verified:
        initialData?.verified !== undefined ? initialData.verified : true,
    },
  });
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      if (isEditMode) {
        await updateMutation.mutateAsync({
          id: initialData._id,
          ...data,
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      form.reset();
      if (onSuccess) {
        console.log("Calling onSuccess callback to close modal");
        onSuccess();
      }
    } catch (error) {
      console.error("Form submission error:", error);
      // Do not call onSuccess here, as the form submission failed
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    isLoadingChapters ||
    isSubmitting;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
        {/* Name and Phone in one row */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="payee"
            rules={{ required: "Payee name is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Payee Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter payee name"
                    {...field}
                    className="h-9"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Phone Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Phone (optional)"
                    {...field}
                    type="tel"
                    className="h-9"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Amount and Currency in one row */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="amount"
            rules={{
              required: "Amount is required",
              min: {
                value: 1,
                message: "Amount must be at least 1",
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Amount</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter amount"
                    type="number"
                    {...field}
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(parseFloat(e.target.value) || "")
                    }
                    className="h-9"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            rules={{ required: "Currency is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Currency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.entries(currencies).map(([code, currency]) => (
                      <SelectItem key={code} value={code}>
                        {currency.symbol} {code} - {currency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Batch and Chapter in one row */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="batch"
            rules={{ required: "Batch is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Batch</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., 2025-Q2"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      setBatch(e.target.value);
                    }}
                    className="h-9"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="chapter"
            rules={{ required: "Chapter is required" }}
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm">Chapter</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Select chapter" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {isLoadingChapters ? (
                      <SelectItem value="loading" disabled>
                        Loading chapters...
                      </SelectItem>
                    ) : chapters?.length > 0 ? (
                      chapters.map((chapter) => (
                        <SelectItem key={chapter._id} value={chapter._id}>
                          {chapter.chapterName}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-chapters" disabled>
                        No chapters available
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Remarks field - compact */}
        <FormField
          control={form.control}
          name="remarks"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm">Remarks</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Additional information (optional)"
                  className="resize-none h-20"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Verified checkbox - compact */}
        <FormField
          control={form.control}
          name="verified"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center space-x-2 space-y-0 rounded-md border p-3">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-sm font-normal">
                Mark as verified
              </FormLabel>
            </FormItem>
          )}
        />

        <Button
          type="submit"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium h-9"
          disabled={isLoading}
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isEditMode ? "Update" : "Add"} HAF Payment
        </Button>
      </form>
    </Form>
  );
}
